import { Lock, ArrowRight } from "lucide-react";

const TOOLS = [
  {
    id: "POC1",
    name: "Portfolio Risk Engine",
    description: "Automated Value-at-Risk (VaR) and stress testing using Monte Carlo simulation across all warehouse data sources.",
    status: "In Development",
    team: "Quant Research",
    eta: "Q2 2025",
    tags: ["Risk", "Monte Carlo", "VaR"],
  },
  {
    id: "POC2",
    name: "ESG Scoring Pipeline",
    description: "Automated ESG score ingestion and portfolio alignment analysis from third-party data providers.",
    status: "Scoping",
    team: "ESG Strategy",
    eta: "Q3 2025",
    tags: ["ESG", "Sustainability", "Compliance"],
  },
  {
    id: "POC3",
    name: "Earnings Transcript Analyser",
    description: "LLM-powered extraction of forward guidance and sentiment signals from quarterly earnings call transcripts.",
    status: "Proof of Concept",
    team: "AI Research",
    eta: "Q3 2025",
    tags: ["NLP", "Earnings", "Sentiment"],
  },
  {
    id: "POC4",
    name: "Automated Regulatory Reporting",
    description: "End-to-end generation of regulatory filings (13F, Form PF) directly from the centralized data warehouse.",
    status: "Planning",
    team: "Compliance",
    eta: "Q4 2025",
    tags: ["Regulatory", "13F", "Compliance"],
  },
];

const STATUS_STYLE = {
  "In Development": "bg-blue-50 text-blue-700",
  "Scoping":        "bg-amber-50 text-amber-700",
  "Proof of Concept": "bg-[#F5F4EE] text-[#A8A67A]",
  "Planning":       "bg-gray-50 text-gray-600",
};

export default function InternalTools() {
  return (
    <div className="page-enter p-6 space-y-6" data-testid="internal-tools-page">
      {/* Header */}
      <div>
        <p className="text-xs text-muted-foreground uppercase tracking-widest mb-1">Workspace</p>
        <h1 className="text-4xl font-bold tracking-tight">Internal Tools</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Upcoming proof-of-concept modules under development by the investment technology team
        </p>
      </div>

      {/* Status summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total POCs", value: "4", sub: "In pipeline" },
          { label: "In Development", value: "1", sub: "Active build" },
          { label: "Available Soon", value: "3", sub: "2025 roadmap" },
          { label: "Est. Team Hours Saved", value: "2,400+", sub: "Per quarter" },
        ].map((s, i) => (
          <div key={i} className="card-olive bg-white border border-border p-4 text-center">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">{s.label}</p>
            <p className="text-2xl font-bold text-[#A8A67A]">{s.value}</p>
            <p className="text-xs text-muted-foreground mt-1">{s.sub}</p>
          </div>
        ))}
      </div>

      {/* POC cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="poc-cards">
        {TOOLS.map(tool => (
          <div
            key={tool.id}
            className="card-olive bg-white border border-border p-6 hover:shadow-md transition-shadow"
            data-testid={`poc-card-${tool.id}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#0A0A0A] flex items-center justify-center flex-shrink-0">
                  <span className="text-[#C4C29A] font-bold font-mono text-sm">{tool.id}</span>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">{tool.name}</h3>
                  <p className="text-xs text-muted-foreground">{tool.team}</p>
                </div>
              </div>
              <span className={`text-[10px] font-semibold px-2 py-1 uppercase tracking-wider ${STATUS_STYLE[tool.status]}`}>
                {tool.status}
              </span>
            </div>

            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              {tool.description}
            </p>

            <div className="flex flex-wrap gap-1.5 mb-4">
              {tool.tags.map(tag => (
                <span key={tag} className="text-[10px] px-2 py-0.5 bg-[#F5F4EE] text-[#A8A67A] font-medium uppercase tracking-wider">
                  {tag}
                </span>
              ))}
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-border">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Lock className="w-3 h-3" />
                <span>ETA: <span className="text-foreground font-medium">{tool.eta}</span></span>
              </div>
              <button
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-[#A8A67A] transition-colors group"
                data-testid={`poc-details-${tool.id}`}
              >
                <span>View Roadmap</span>
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Bottom CTA */}
      <div className="bg-[#0A0A0A] border border-white/10 p-6 flex items-center justify-between">
        <div>
          <p className="font-semibold text-white">Have an idea for a new tool?</p>
          <p className="text-sm text-white/50 mt-0.5">Submit a POC proposal to the Investment Technology team</p>
        </div>
        <button
          className="flex items-center gap-2 bg-[#A8A67A] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#8E8C64] transition-colors"
          data-testid="propose-poc-btn"
        >
          Submit Proposal
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
